import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TASK_PRIORITIES } from "@/config/taskOptions";
import { COMM_TYPES, VISIBILITIES } from "@/config/commOptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Zap, ListChecks, StickyNote, Phone, Mail, CalendarPlus, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const tomorrow = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);

const ACTIONS = {
  add_task: { label: "Add task", icon: ListChecks, title: "Add a task" },
  follow_up: { label: "Schedule follow-up", icon: CalendarPlus, title: "Schedule a follow-up" },
  add_note: { label: "Add note", icon: StickyNote, title: "Add a note" },
  log_call: { label: "Log call", icon: Phone, title: "Log a phone call" },
  log_email: { label: "Log email", icon: Mail, title: "Log an email" },
};

export default function QuickActions({ defaultRelatedType = null, defaultRelatedId = null, onCreated, label = "Quick actions" }) {
  const fixedRelated = !!defaultRelatedId;
  const [leads, setLeads] = useState([]);
  const [cases, setCases] = useState([]);
  const [action, setAction] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [related, setRelated] = useState("none");
  const [form, setForm] = useState({});

  useEffect(() => {
    if (fixedRelated) return;
    api.get("/leads", { params: { limit: 100 } }).then((r) => setLeads(r.data.items || [])).catch(() => {});
    api.get("/cases", { params: { limit: 100 } }).then((r) => setCases(r.data.items || [])).catch(() => {});
  }, [fixedRelated]);

  const start = (key) => {
    setAction(key);
    setRelated("none");
    if (key === "add_task") setForm({ title: "", priority: "normal", due_date: "", notes: "" });
    else if (key === "follow_up") setForm({ title: "Follow up", priority: "high", due_date: tomorrow(), notes: "" });
    else if (key === "add_note") setForm({ body: "", visibility: "internal" });
    else setForm({ subject: "", summary: "", direction: "outbound", visibility: "internal" });
    setOpen(true);
  };

  const relatedFields = () => {
    if (fixedRelated) return { related_type: defaultRelatedType, related_id: defaultRelatedId };
    if (related === "none") return {};
    const [type, id] = related.split(":");
    return { related_type: type, related_id: id };
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const rel = relatedFields();
      if (action === "add_task" || action === "follow_up") {
        if (!form.title?.trim()) { toast.error("Title is required"); setBusy(false); return; }
        await api.post("/tasks", { ...form, ...rel, status: "not_started" });
        toast.success(action === "follow_up" ? "Follow-up scheduled" : "Task created");
      } else if (action === "add_note") {
        if (!form.body?.trim()) { toast.error("Note can't be empty"); setBusy(false); return; }
        await api.post("/notes", { ...form, ...rel });
        toast.success("Note added");
      } else {
        await api.post("/communications", { ...form, communication_type: action === "log_call" ? "call" : "email", ...rel });
        toast.success("Communication logged");
      }
      setOpen(false);
      onCreated && onCreated(action);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const A = action ? ACTIONS[action] : null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="text-white" style={{ backgroundColor: "var(--mmm-navy)" }} data-testid="quick-actions-trigger">
            <Zap size={16} className="mr-1.5" /> {label} <ChevronDown size={15} className="ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel>Quick actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {Object.entries(ACTIONS).map(([key, a]) => {
            const Icon = a.icon;
            return (
              <DropdownMenuItem key={key} onClick={() => start(key)} data-testid={`quick-action-${key}`}>
                <Icon size={15} className="mr-2 text-[#1C4358]" /> {a.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="quick-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              {A && <A.icon size={18} className="text-[#1C4358]" />} {A?.title}
            </DialogTitle>
            <DialogDescription>Capture this in seconds and keep moving.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {(action === "add_task" || action === "follow_up") && (
              <>
                <div><Label className="mb-1.5 block text-[13px]">Title *</Label><Input value={form.title || ""} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="qa-task-title" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="mb-1.5 block text-[13px]">Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm((p) => ({ ...p, priority: v }))}>
                      <SelectTrigger data-testid="qa-task-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>{TASK_PRIORITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="mb-1.5 block text-[13px]">Due date</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))} data-testid="qa-task-due" /></div>
                </div>
              </>
            )}
            {action === "add_note" && (
              <>
                <div><Label className="mb-1.5 block text-[13px]">Note *</Label><Textarea rows={3} value={form.body || ""} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} data-testid="qa-note-body" /></div>
                <div><Label className="mb-1.5 block text-[13px]">Visibility</Label>
                  <Select value={form.visibility} onValueChange={(v) => setForm((p) => ({ ...p, visibility: v }))}>
                    <SelectTrigger data-testid="qa-note-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>{VISIBILITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}
            {(action === "log_call" || action === "log_email") && (
              <>
                <div><Label className="mb-1.5 block text-[13px]">Subject</Label><Input value={form.subject || ""} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} data-testid="qa-comm-subject" /></div>
                <div><Label className="mb-1.5 block text-[13px]">Summary</Label><Textarea rows={3} value={form.summary || ""} onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))} data-testid="qa-comm-summary" /></div>
                <div><Label className="mb-1.5 block text-[13px]">Direction</Label>
                  <Select value={form.direction} onValueChange={(v) => setForm((p) => ({ ...p, direction: v }))}>
                    <SelectTrigger data-testid="qa-comm-direction"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="outbound">Outbound</SelectItem><SelectItem value="inbound">Inbound</SelectItem></SelectContent>
                  </Select>
                </div>
              </>
            )}
            {!fixedRelated && (
              <div><Label className="mb-1.5 block text-[13px]">Related to (optional)</Label>
                <Select value={related} onValueChange={setRelated}>
                  <SelectTrigger data-testid="qa-related-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    <SelectItem value="none">General (not linked)</SelectItem>
                    {cases.length > 0 && <SelectItem value="__cases__" disabled className="text-xs font-semibold opacity-70">Family cases</SelectItem>}
                    {cases.map((c) => <SelectItem key={c.id} value={`cases:${c.id}`}>{c.case_name || c.title || c.family_name || "Case"}</SelectItem>)}
                    {leads.length > 0 && <SelectItem value="__leads__" disabled className="text-xs font-semibold opacity-70">Leads</SelectItem>}
                    {leads.map((l) => <SelectItem key={l.id} value={`leads:${l.id}`}>{l.name || [l.first_name, l.last_name].filter(Boolean).join(" ") || "Lead"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="qa-save-button">{busy ? "Saving..." : "Save"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
